import {ui, site, nav, IComment, NotificationsForWorkspace, CatalogStore, globalStores} from '@stores'
import {React, lockr, _} from '@api'
import {synchronizeStores} from '@stores/SynchronizeStores'
import * as css from './UserNotificationsStore.css'
import { authStore } from '@utils'
import { CatalogInstanceChildStore } from '@stores/catalog/CatalogInstanceChildStore'

/**
 * TODO - should be one per active catalog in the flex layout
 */
export class UserNotificationsStore extends CatalogInstanceChildStore {
  constructor(public catalog: CatalogStore) {
    super(catalog)

    if (!catalog.isGlobalStore) throw new Error(`This class can only be used as a singleton in the global application store`)

    synchronizeStores(async () => {
      if (catalog !== globalStores.catalog) {
        throw new Error(`UserNotificationsStore should be a singleton!`)
      }

      setInterval(() => {
        try {
          if (!catalog.loadingCounts) {
            this.getRecentNotifications()
          }
        }
        catch (err) {
          console.error(err)
        }
      }, ui.settings.notificationPollInterval)
    })
  }

  /**
   * To avoid tabs colliding with each other via localStorage
   * @private
   */
  private _lastNotificationTimestamp

  get lastNotificationTimestamp() {
    if (!this._lastNotificationTimestamp) {
      this._lastNotificationTimestamp = lockr.get<number>(`lastNotificationTimestamp`)
    }

    return this._lastNotificationTimestamp
  }

  set lastNotificationTimestamp(v: number) {
    lockr.set(`lastNotificationTimestamp`, v)
    this._lastNotificationTimestamp = v
  }

  private getRecentNotifications = async () => {
    const {stores, catalog, es, stores: {users}, workspaces} = this

    if (!authStore.userIsAuthenticated || !workspaces.availableWorkspaces.length || site.userIsIdle) return

    const userWorkspaceConfig = (stores.currentUser.userSettings?.workspaces ?? {})

    const lastTimestamp = this.lastNotificationTimestamp ?? new Date().getTime(),
      nextTimestamp = new Date().getTime()

    // Query for latest changes
    const {aggregations} = await es.search({
      meta: 'UserNotificationsStore.getRecentNotifications()',
      index: `${es.indexes.comment},${es.indexes.changeHistory}`,
      body: {
        size: 0,
        aggs: workspaces.availableWorkspaces.filter(ws => ws.catalog?.index).reduce((acc, ws) => {
          const {catalog: {index}} = ws
          return _.assign(acc, {
              [index]: {
                filter: {
                  bool: {
                    must: [
                      {term: {_index: es.indexes.changeHistory}},
                      {term: {'index.keyword': index}},
                      {range: {timestamp: {gt: lastTimestamp}}}
                    ],
                    must_not: [
                      {term: {'userId.keyword': users.current.id}}
                    ]
                  }
                },
                aggs: {
                  hits: {
                    top_hits: {
                      size: 10,
                      _source: ['timestamp', 'userId', 'id', 'operation', 'payload'],
                      sort: {timestamp: {order: 'asc'}}
                    }
                  }
                }
              },
              [`${index}.comments`]: {
                filter: {
                  bool: {
                    must: [
                      {term: {_index: es.indexes.comment}},
                      {range: {timestamp: {gt: lastTimestamp}}},
                      {term: {'index.keyword': index}}
                    ],
                    must_not: [
                      {term: {'userId.keyword': users.current.id}}
                    ]
                  }
                },
                aggs: {
                  hits: {
                    top_hits: {
                      size: 10,
                      _source: ['timestamp', 'userId', 'indexItemId'],
                      sort: {timestamp: {order: 'asc'}}
                    }
                  }
                }
              }
            })
        }, {})
      }
    }, {log: {query: false, result: false}})

    const changes = _.keys(aggregations).filter(k => aggregations[k].doc_count > 0).reduce((acc, k) => _.assign(acc, {[k]: aggregations[k].hits.hits.hits}), {})

    if (!_.isEmpty(changes) || !this.lastNotificationTimestamp) {
      this.lastNotificationTimestamp = nextTimestamp
    }

    // Translate the results
    if (!_.isEmpty(changes)) {
      // get a list of changes by catalog itemId
      const byIndexAndId = _.keys(changes).reduce((acc, k) => {
        const isComments = k.endsWith('.comments'),
          index = !isComments ? k : k.substring(0, k.indexOf('.')),
          ws = workspaces.byIndexScalar[index],
          values = changes[k].map(v => v._source),
          distinctItemIds = _.groupBy(values, v => isComments ? v.indexItemId : v.id)

        //console.log(k, values, distinctItemIds)
        return _.assign(acc, {
          [index]: {
            ...(acc[index] ?? {}),
            ..._.keys(distinctItemIds).reduce((acc2, id) => _.assign(acc2, {
              [id]: {
                [isComments ? 'comments' : 'events']: distinctItemIds[id],
                ...((acc[index] ?? {})[id])
              }
            }), {})
          }
        })
      }, {})

      const dataForId = await Promise.all(
        _.keys(byIndexAndId).map(
          async index => {
            const ids = _.keys(byIndexAndId[index]),
              ws = workspaces.byIndexScalar[index],
              dataMap = catalog.dataMapFor(ws.dataMap),
              items = es.unwrapHitsResult(await es.search({
                index,
                size: ids.length,
                body: {
                  _source: _.uniq([..._.values(dataMap), ...(index == catalog.index ? catalog.selectedImageSourceFields : [...(ws.catalog.imageSources[0].fields ?? []), ws.catalog.imageSources[0].field])].filter(_.identity)),
                  query: {terms: {id: ids}}
                }
              }, {log: {query: false}})).items
            // name = _.get(item, dataMap.title)

            //console.log(ids, items)
            return {index, items: items.reduce((acc, v) => _.assign(acc, {[v._id]: v}), {})}
          })),
        dataForIdByIndex = dataForId.reduce((acc, v) => _.assign(acc, {[v.index]: v.items}), {})

      //console.log({byIndexAndId, dataForId, dataForIdByIndex})

      /*const r = await Promise.all(
         _.keys(changes).map(async k => {
           const changesForIndex = changes[k] as Array<IComment>
           if (k.endsWith('.comments')) {
             const ws = workspaces.byIndex[k.substring(0, k.indexOf('.'))],
               dataMap = catalog.dataMapFor(ws.dataMap)

             // So and so commented on object XYZ
             const commentsByItemId = _.keyBy(changes[k].map(v => v._source), v => v.indexItemId)
             return await Promise.all(
               changes[k].map(async ({_source: {userId, indexItemId}}, i) => {
                 const item = await es.get({index: ws.catalog.index, id: indexItemId, _source: [dataMap.title]}),
                   name = _.get(item, dataMap.title)

                 return (
                   <div className={css.notification} key={`${k}.${i}`}>
                     <span className={css.title}>{userId} commented on <a target='_blank'
                                                                          href={nav.urlFor_string({
                                                                            workspace: ws._id,
                                                                            queryParams: {
                                                                              ...sidebar.queryParamsFor({
                                                                                right: {
                                                                                  open: true,
                                                                                  tab: 'comments',
                                                                                  path: [{where: [{value: indexItemId}]}]
                                                                                }
                                                                              })
                                                                            }
                                                                          })}>
                       '{name || ws.itemName}'
                     </a>
                     </span>
                   </div>
                 )
               }))

             console.log(commentsByItemId)
           } else {
             // So and so changed values/created/delete object XYZ
             // id can be an array
             const flattened = _.flatMap(changes[k].map(({_source}) => _.isArray(_source.id) ? _source.id.map(id => ({..._source, id})) : _source)) as Array<ICatalogItemChangeEvent & { id: string }>,
               changesByItemId = _.keyBy(flattened, v => v.id),
               ws = workspaces.byIndex[k]

             const dataMap = catalog.dataMapFor(ws.dataMap)

             console.log(changesByItemId)

             return await Promise.all(_.keys(changesByItemId).map(async itemId => {
               const change = changesByItemId[itemId],
                 verb = verbForChangeEvent(change)

               let name: string
               if (change.operation == 'DELETE') {
                 name = _.get(change.payload.data.before, dataMap.title)
               } else {
                 const r = await es.get({index: k, id: itemId, _source: [dataMap.title]})
                 name = _.get(r, dataMap.title)
               }

               return (
                 <div key={itemId} className={css.notification}>
                 <span className={css.title}>
                   {change.userId} {verb} <a target='_blank'
                                             href={nav.urlFor_string({
                                               workspace: ws._id,
                                               queryParams: {...sidebar.queryParamsFor({right: {open: true, path: [{where: [{value: itemId}]}]}})}
                                             })}>
                     '{name || ws.itemName}'
                   </a>
                 </span>
                   {/!*<div className={css.details}>*!/}
                   {/!*  Details*!/}
                   {/!*</div>*!/}
                 </div>
               )
             }))
           }
         }))
 */

      if (!_.isEmpty(byIndexAndId[catalog.index])) {
        catalog.refetch(catalog.index)
      }

      const workspacesToDisplay = workspaces.availableWorkspaces.filter(ws => ws.catalog?.index != null && byIndexAndId[ws.catalog.index] && userWorkspaceConfig[ws._id]?.notifications?.desktop)

      workspacesToDisplay.length > 0 && catalog.dialogs.showToastMessage(<div className={css.notifications}>
        {workspacesToDisplay.map(ws =>
          <NotificationsForWorkspace workspace={ws} key={ws._id} changes={byIndexAndId[ws.catalog.index]} data={dataForIdByIndex[ws.catalog.index]}/>
        )}
      </div>)
    }
  }
}



// WEBPACK FOOTER //
// ./src/stores/notifications/UserNotificationsStore.tsx